<!--
  Add / attach a related record over the page it is listed on, instead of
  routing away to `…/:childModelKey/new` and back.

  The counterpart to BreadModelEditDialog, for the OTHER direction of the read
  page's relation tabs. It posts to the SAME store URL
  PageServiceModelChildAdd.vue does, built the same way, with BreadModelForm as
  the form in both places — only where the form lives changes.

  One thing it does NOT do that the page does: re-read the parent record. The
  page had to, because it was reached by URL and knew the parent only by id;
  the tab that hosts this dialog already holds the parent record it was handed,
  and that record is what seeds `form.data` — which is what preselects and locks
  the parent's own selector (see BreadModelForm.isParentLockedField).

  The /…/new route still resolves, for links that point straight at it.
-->
<template>
  <v-dialog
    v-model="isOpen"
    max-width="680"
    :persistent="api.isLoading"
    content-class="cad-dialog"
    @click:outside="close"
  >
    <div v-if="isOpen" class="cad-modal">
      <div class="cad-modal__head">
        <div class="cad-modal__identity">
          <h2 class="cad-modal__title">{{ title }}</h2>
          <p v-if="parentTitle" class="cad-modal__subtitle">{{ parentTitle }}</p>
        </div>
        <button class="cad-modal__close" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="cad-modal__body">
        <BreadModelForm
          ref="form"
          embedded
          action="add"
          :api="api"
          :model="childModel"
          :parentModel="parentModel"
        />
      </div>

      <div class="cad-modal__foot">
        <button
          class="cad-modal__btn cad-modal__btn--ghost"
          type="button"
          @click="close"
        >
          Cancel
        </button>
        <v-btn
          class="cad-modal__btn cad-modal__btn--primary"
          depressed
          :loading="api.isLoading"
          @click="submit"
        >
          {{ submitLabel }}
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Api from "@/objects/api";
import BreadModelForm from "@/components/common/BreadModelForm.vue";
import Model from "@/objects/model";
import Service from "@/objects/service";

export default {
  name: "BreadModelChildAddDialog",
  components: {
    BreadModelForm,
  },
  props: {
    /** v-model: whether the dialog is open. */
    value: {
      type: Boolean,
      default: false,
    },
    /** The record the relation hangs off — the tab's own parent. */
    parent: {
      type: Object,
      default: () => ({}),
    },
    parentModel: {
      type: Object,
      required: true,
    },
    childModelKey: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: "New record",
    },
    submitLabel: {
      type: String,
      default: "Create",
    },
    successMessage: {
      type: String,
      default: "Created successfully.",
    },
  },
  data: () => ({
    api: new Api(),
    childModel: new Model(),
    childService: new Service(),
  }),
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(open) {
        this.$emit("input", open);
      },
    },
    /** Names the record being attached TO, so the dialog says where it lands. */
    parentTitle() {
      const textColumn = this.parentModel.column && this.parentModel.column.text;
      const title = textColumn ? this.parent[textColumn] : this.parent.name;
      return title || null;
    },
  },
  watch: {
    value(open) {
      if (!open) return;
      // Clear the previous attempt's error banner. NOT via callbackReset() —
      // despite the name that is Api's pre-flight hook and sets isLoading TRUE,
      // which through `:persistent` would open the dialog already spinning and
      // refusing to close.
      this.api.isError = false;
      this.api.error = null;
      // The form only exists once the dialog's content has rendered. Seeding it
      // here rather than passing initial data keeps BreadModelForm's contract
      // identical to the one the edit dialog and the add page use.
      this.$nextTick(() => {
        if (!this.$refs.form) return;
        // A clone, so the form's own buffer can't write through to the record
        // the tab behind the dialog is rendering from.
        this.$refs.form.setData({
          [this.$_ucfirst(this.parentModel.key)]: this.$_.cloneDeep(this.parent),
        });
      });
    },
    childModelKey: "resolveChildModel",
  },
  created() {
    this.resolveChildModel();
  },
  methods: {
    resolveChildModel() {
      this.childModel.getByKey(this.childModelKey);
      if (this.childModel.serviceKey)
        this.childService.set("key", this.childModel.serviceKey);

      this.api.setMethod("POST");
      this.api.setUrl(this.storeUrl());
      this.api.setCallbackCompleted(() => {
        this.isOpen = false;
        this.$emit("created", { childModelKey: this.childModel.key });
        this.$store.dispatch("showMessage", {
          message: this.successMessage,
          messageType: "success",
          timeout: 2000,
        });
      });
    },
    storeUrl() {
      if (this.childModel.apiUrls.apiStoreUrl != "") {
        return `${this.$service[this.childService.key]}${
          this.childModel.apiUrls.apiStoreUrl
        }`;
      }
      return `${
        this.$service[this.childService.key]
      }/v1/en/console/tabulator/model/${this.childModel.key}/add`;
    },
    close() {
      if (this.api.isLoading) return;
      this.isOpen = false;
    },
    submit() {
      if (this.$refs.form) this.$refs.form.validate();
    },
  },
};
</script>

<style scoped>
/* Same three tiers as the read page's edit dialog: chrome on --app-card, the
   form area a step back on --app-surface, the field wells a step forward again.
   Every band is painted — a transparent one shows the overlay scrim through the
   panel. Token-only colour, so dark needs no companion block. */
.cad-modal {
  background: var(--app-card);
  font-family: var(--tbl-font);
}

.cad-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.cad-modal__identity {
  min-width: 0;
}

.cad-modal__title {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
}

.cad-modal__subtitle {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--app-text-faint);
  margin: 3px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cad-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--app-text-faint);
  transition: all 0.15s ease;
}

.cad-modal__close:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

/* Capped so a model with many fields scrolls the FORM rather than growing the
   dialog past the viewport and pushing its own Confirm button off-screen. */
.cad-modal__body {
  padding: 22px 24px 26px;
  max-height: 60vh;
  overflow-y: auto;
  background: var(--app-surface);
}

.cad-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--app-border);
  background: var(--app-card);
}

.cad-modal__btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 10px;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  transition: all 0.15s ease;
}

.cad-modal__btn--ghost {
  border: 1px solid var(--app-border-strong);
  background: transparent;
  color: var(--app-text-muted);
}

.cad-modal__btn--ghost:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

.cad-modal__btn--primary.v-btn {
  background: var(--app-accent) !important;
  color: var(--app-accent-fg) !important;
  box-shadow: none;
}

.cad-modal__btn--primary.v-btn:hover {
  background: var(--app-accent-strong) !important;
}

@media (max-width: 599px) {
  .cad-modal__head,
  .cad-modal__foot {
    padding: 12px;
  }

  .cad-modal__body {
    padding: 14px 12px 18px;
    max-height: 66vh;
  }

  .cad-modal__btn {
    flex: 1;
  }
}
</style>

<style>
/* Unscoped: VDialog moves this content out of the component, and `content-class`
   lands on an element it owns rather than on our root, so a scoped selector
   never reaches either. */
.v-dialog.cad-dialog {
  border-radius: 14px;
  background: var(--app-card);
  border: 1px solid var(--app-border);
  box-shadow: 0 24px 60px var(--app-shadow-deep, rgba(0, 0, 0, 0.35));
}

/* Vuetify paints every sheet its built-in near-black in the dark theme; the
   embedded form's card is meant to be a wrapper, not a surface. */
.v-dialog.cad-dialog .v-card.bmf__card--embedded {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
